/**
 * Canvas/SVG renderer composable.
 *
 * Converts design items into canvas images for:
 * - Preview rendering
 * - Save/export (toDataURL)
 * - Add to cart (product mockup images)
 *
 * Based on svg.js module: canvas rendering + composite product image generation.
 */
import { useDesignStore, type ViewName } from '../stores/designStore';
import { useProductStore } from '../stores/productStore';
import { useConfigStore, type GarmentLayerConfig } from '../stores/configStore';
import { useVectorExport } from './useVectorExport';
import type { DesignItem } from '@core/types';
import { eventBus } from '@core/EventBus';
import { drawImageItem, isLayerVisible, loadImageBitmap } from '@core/imagePipeline';
import {
  resolvePrintMethodPackForItem,
  getImageRasterizers,
  resolvePrintingMethodProviderId,
  resolvePrintAreaShape,
} from '@core/extensionRegistry';
import {
  resolvePrintAreaPx,
  resolveViewPhysicalSizesCm,
  inferPrintAreaShape,
  resolvePrintAreaShapeParams,
} from '../utils/printArea';
import type { Pinia } from 'pinia';
import { shouldTintLayer, tintHexAtSlot } from '@core/garmentTint';
import { drawTintedGarmentLayer } from '@core/garmentCanvas';
import { bootstrapBuiltinPrintAreaShapes } from '@core/printAreaShapes';

/** Safely parse a JSON string that may use single quotes instead of double quotes. */
function safeParseJson(str: string, fallback: unknown = null): unknown {
  try {
    return JSON.parse(str.replace(/'/g, '"'));
  } catch {
    return fallback;
  }
}

/** Numeric z-order; legacy stores `"auto"` which must not sort as NaN. */
function layerSortKey(layer: Record<string, unknown>): number {
  const z = layer.zIndex;
  if (z === 'auto' || z === '' || z == null) return 0;
  const n = Number(z);
  return Number.isFinite(n) ? n : 0;
}

/** Pixel layer rows from color.views (array JSON, object map, or non-URL string that failed parse). */
function tryParseViewLayerRows(rawView: unknown): Record<string, unknown>[] | null {
  if (Array.isArray(rawView)) {
    return rawView.length ? (rawView as Record<string, unknown>[]) : null;
  }
  if (typeof rawView !== 'string') return null;
  const trimmed = rawView.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    const parsed = safeParseJson(trimmed, null);
    if (Array.isArray(parsed) && parsed.length) {
      return parsed as Record<string, unknown>[];
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const vals = Object.values(parsed as Record<string, unknown>);
      return vals.length ? (vals as Record<string, unknown>[]) : null;
    }
  }
  return null;
}

function resolveProductImageSrc(imgSrc: string, veyopUrl: string, siteUrl: string): string {
  const s = imgSrc.trim();
  if (!s) return '';
  if (/^(https?:)?\/\//i.test(s) || s.startsWith('data:')) {
    return s.startsWith('//') ? `https:${s}` : s;
  }
  if (s.startsWith('/')) {
    return `${(siteUrl || '').replace(/\/$/, '')}${s}`;
  }
  const v = (veyopUrl || '').replace(/\/$/, '');
  return `${v}/${s.replace(/^\//, '')}`;
}

interface MaskImageConfig {
  url: string;
  top: number;
  left: number;
  width: number;
  height: number;
  z?: number;
}

function clampMaskNumber(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function normalizeMaskImage(raw: unknown, index: number): MaskImageConfig | null {
  if (typeof raw === 'string' && raw.trim()) {
    return { url: raw.trim(), top: 0, left: 0, width: 100, height: 100, z: 10000 + index };
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const url = typeof record.url === 'string' ? record.url.trim() : '';
  if (!url) return null;
  return {
    url,
    top: clampMaskNumber(record.top, -450, 450, 0),
    left: clampMaskNumber(record.left, -450, 450, 0),
    width: clampMaskNumber(record.width, 0.25, 500, 100),
    height: clampMaskNumber(record.height, 0.25, 500, 100),
    z: Number.isFinite(Number(record.z)) ? Number(record.z) : 10000 + index,
  };
}

function maskSourceFromConfig(raw: unknown): Record<string, unknown> {
  let data = raw;
  if (typeof data === 'string' && data.trim()) {
    data = safeParseJson(data, {});
  }
  return data && typeof data === 'object' && !Array.isArray(data)
    ? data as Record<string, unknown>
    : {};
}

function masksForView(configData: Record<string, unknown>, view: ViewName): MaskImageConfig[] {
  const source = maskSourceFromConfig(configData.maskImages ?? configData.mask_images);
  const list = source[view];
  if (!Array.isArray(list)) return [];
  return list
    .map((item, index) => normalizeMaskImage(item, index))
    .filter((item): item is MaskImageConfig => item !== null)
    .sort((a, b) => Number(a.z ?? 0) - Number(b.z ?? 0));
}

async function drawMaskImagesOnCanvas(
  ctx: CanvasRenderingContext2D,
  configData: Record<string, unknown>,
  view: ViewName,
  canvasWidth: number,
  canvasHeight: number,
) {
  const masks = masksForView(configData, view);
  if (!masks.length) return;
  const veyop = String(configData.veyopUrl || '').replace(/\/$/, '');
  const site = String(configData.siteUrl || '').replace(/\/$/, '');
  for (const mask of masks) {
    const src = resolveProductImageSrc(mask.url, veyop, site);
    if (!src) continue;
    try {
      const img = await loadImage(src);
      ctx.drawImage(
        img,
        (mask.left / 100) * canvasWidth,
        (mask.top / 100) * canvasHeight,
        (mask.width / 100) * canvasWidth,
        (mask.height / 100) * canvasHeight,
      );
    } catch {
      // Broken mask images should not block cart/order preview generation.
    }
  }
}

/** Match DesignView.resolveViewImage: JSON with img nodes, URL, or relative path. */
function resolveColorViewImageUrl(
  rawView: string,
  veyopUrl: string,
  siteUrl: string,
): string {
  const raw = (rawView ?? '').trim();
  if (!raw) return '';
  if (raw.startsWith('[') || raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw.replace(/'/g, '"'));
      const nodes = Array.isArray(parsed) ? parsed : Object.values(parsed ?? {});
      const imageNode = (nodes as Array<Record<string, unknown>>).find((node) => {
        if (!node || typeof node !== 'object') return false;
        const img = node.img;
        return typeof img === 'string' && img.length > 0;
      });
      if (imageNode && typeof imageNode.img === 'string') {
        return resolveProductImageSrc(imageNode.img, veyopUrl, siteUrl);
      }
    } catch {
      return '';
    }
    return '';
  }
  return resolveProductImageSrc(raw, veyopUrl, siteUrl);
}

function fallbackBackgroundUrlView(
  view: ViewName,
  viewImages: Record<string, string> | undefined,
  productImage: string,
): string | null {
  const fromCfg = viewImages?.[view]?.trim();
  if (fromCfg) return fromCfg;
  if (view === 'front' && productImage.trim()) {
    return productImage.trim();
  }
  return null;
}

function drawImageContain(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
) {
  const iw = Math.max(1, img.naturalWidth || img.width);
  const ih = Math.max(1, img.naturalHeight || img.height);
  const scale = Math.min(dw / iw, dh / ih);
  const w = iw * scale;
  const h = ih * scale;
  const x = dx + (dw - w) / 2;
  const y = dy + (dh - h) / 2;
  ctx.drawImage(img, x, y, w, h);
}

export function useCanvasRenderer(pinia?: Pinia | null) {
  // Storefront bundles (Quick Personalizer, Make It Mine) render without the designer's
  // bootstrap; without the built-in shapes a rounded print area would not be clipped.
  bootstrapBuiltinPrintAreaShapes();
  const p = pinia ?? undefined;
  const design = p ? useDesignStore(p) : useDesignStore();
  const product = p ? useProductStore(p) : useProductStore();
  const config = p ? useConfigStore(p) : useConfigStore();
  const vectorExport = useVectorExport(p);

  /**
   * Render all items of a view onto a canvas matching the design area size.
   */
  async function renderView(
    view: ViewName,
    purpose: 'preview' | 'production' = 'preview',
    outputScale = 1,
  ): Promise<HTMLCanvasElement | null> {
    const area = product.product?.areas[view];
    if (!area) return null;

    const parsedArea =
      typeof area === 'string' ? (safeParseJson(area) as Record<string, unknown>) : area;
    if (!parsedArea) return null;

    const stageW = Math.max(1, Number(config.data.items.boxWidth) || 500);
    const stageH = Math.max(1, Number(config.data.items.boxHeight) || 500);
    const rect = resolvePrintAreaPx(parsedArea, stageW, stageH);
    if (!rect) return null;

    const areaW = rect.width;
    const areaH = rect.height;

    const items = (design.items[view] ?? []).filter((item) => item.placementSpace !== 'product-stage');
    if (!items.length) return null;

    eventBus.emit('start.canvas.design' as string, view);

    // Sort by zIndex ascending; skip hidden layers
    const sorted = [...items]
      .filter((item) => isLayerVisible(item))
      .sort((a, b) => a.zIndex - b.zIndex);

    const canvas = document.createElement('canvas');
    const renderScale = Math.max(1, Math.min(8, Number(outputScale) || 1));
    canvas.width = Math.max(1, Math.round(areaW * renderScale));
    canvas.height = Math.max(1, Math.round(areaH * renderScale));
    const ctx = canvas.getContext('2d')!;
    if (renderScale !== 1) ctx.scale(renderScale, renderScale);

    // Clip to the print area's configured shape (rect is a harmless no-op clip).
    const shapeId = inferPrintAreaShape(parsedArea);
    if (shapeId !== 'rect') {
      applyClip(ctx, areaW, areaH, shapeId, resolvePrintAreaShapeParams(parsedArea));
    }

    for (const item of sorted) {
      await renderItem(ctx, item, view, purpose);
    }

    eventBus.emit('end.canvas.design' as string, view);
    return canvas;
  }

  /**
   * Render a single item onto a 2D context.
   */
  async function renderItem(
    ctx: CanvasRenderingContext2D,
    item: DesignItem,
    view: ViewName,
    purpose: 'preview' | 'production' = 'preview',
  ): Promise<void> {
    const { top, left, width, height } = item;
    const ex = item as Record<string, unknown>;
    const angleDeg = Number(item.angle ?? ex.rotate ?? 0);
    const flipX = Number(ex.flipX) === -1 ? -1 : 1;
    const flipY = Number(ex.flipY) === -1 ? -1 : 1;

    ctx.save();
    try {
      ctx.translate(left + width / 2, top + height / 2);
      if (angleDeg) {
        ctx.rotate((angleDeg * Math.PI) / 180);
      }
      if (flipX !== 1 || flipY !== 1) {
        ctx.scale(flipX, flipY);
      }
      ctx.translate(-width / 2, -height / 2);

      const renderBase = async (): Promise<string | null> => {
        const base = document.createElement('canvas');
        base.width = Math.max(1, Math.round(width));
        base.height = Math.max(1, Math.round(height));
        const baseCtx = base.getContext('2d');
        if (!baseCtx) return null;
        const drew = await renderBaseItem(baseCtx, item, width, height);
        return drew ? base.toDataURL('image/png') : null;
      };

      const printPack = resolvePrintMethodPackForItem(item);
      if (printPack?.rasterizeForPrint) {
        const dataUrl = await printPack.rasterizeForPrint(item, {
          width,
          height,
          scale: purpose === 'production' ? 4 : 2,
          purpose,
          view,
          activePrintCode: product.selectedPrintingCode,
          activePrintMethodPack: resolvePrintingMethodProviderId(product.selectedPrinting),
          renderBase,
        });
        if (dataUrl) {
          const img = await loadImage(dataUrl);
          ctx.drawImage(img, 0, 0, width, height);
          return;
        }
      }

      await renderBaseItem(ctx, item, width, height);
    } catch {
      // Skip item on render error
    } finally {
      ctx.restore();
    }
  }

  /** Render a layer without applying a print-method pack. */
  async function renderBaseItem(
    ctx: CanvasRenderingContext2D,
    item: DesignItem,
    width: number,
    height: number,
  ): Promise<boolean> {
    if (item.type === 'text') {
        const svgStr = await vectorExport.generateSvg(item);
        if (svgStr) {
          const encoded =
            'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);
          const img = await loadImage(encoded);
          ctx.drawImage(img, 0, 0, width, height);
          return true;
        }
        return false;
      }

      // Raster clipart/upload: unified image pipeline (crop, adjustments, effects).
      // Addon-registered rasterizers (e.g. specialised effects) take precedence over
      // the built-in pipeline when they match the item.
      for (const rasterizer of getImageRasterizers()) {
        if (!rasterizer.matches(item)) continue;
        const dataUrl = await rasterizer.rasterize(item, { width, height });
        if (dataUrl) {
          const img = await loadImage(dataUrl);
          ctx.drawImage(img, 0, 0, width, height);
          return true;
        }
      }

      const drew = await drawImageItem(
        ctx,
        item,
        width,
        height,
        (raw) => vectorExport.resolveAssetUrl(raw),
      );
      if (drew) return true;

      const svgStr = await vectorExport.generateSvg(item);
      if (svgStr) {
        const encoded =
          'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);
        const img = await loadImage(encoded);
        ctx.drawImage(img, 0, 0, width, height);
        return true;
      }
      return false;
  }

  /** Render only product-stage items to a transparent full-stage canvas. */
  async function renderStageItemsCanvas(
    view: ViewName,
    purpose: 'preview' | 'production' = 'preview',
    outputScale = 1,
  ): Promise<HTMLCanvasElement | null> {
    const items = (design.items[view] ?? [])
      .filter((item) => item.placementSpace === 'product-stage' && isLayerVisible(item))
      .sort((a, b) => a.zIndex - b.zIndex);
    if (!items.length) return null;
    const stageW = Math.max(1, Number(config.data.items.boxWidth) || 500);
    const stageH = Math.max(1, Number(config.data.items.boxHeight) || 500);
    const renderScale = Math.max(1, Math.min(8, Number(outputScale) || 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(stageW * renderScale));
    canvas.height = Math.max(1, Math.round(stageH * renderScale));
    const ctx = canvas.getContext('2d')!;
    if (renderScale !== 1) ctx.scale(renderScale, renderScale);
    for (const item of items) await renderItem(ctx, item, view, purpose);
    return canvas;
  }

  /** Transparent artwork in full-stage coordinates, with print-area items offset and clipped. */
  async function renderStageArtwork(
    view: ViewName,
    purpose: 'preview' | 'production' = 'preview',
    outputScale = 1,
  ): Promise<HTMLCanvasElement | null> {
    const stageW = Math.max(1, Number(config.data.items.boxWidth) || 500);
    const stageH = Math.max(1, Number(config.data.items.boxHeight) || 500);
    const stageItems = await renderStageItemsCanvas(view, purpose, outputScale);
    const printItems = await renderView(view, purpose, outputScale);
    if (!stageItems && !printItems) return null;
    const renderScale = Math.max(1, Math.min(8, Number(outputScale) || 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(stageW * renderScale));
    canvas.height = Math.max(1, Math.round(stageH * renderScale));
    const ctx = canvas.getContext('2d')!;
    if (stageItems) ctx.drawImage(stageItems, 0, 0, canvas.width, canvas.height);
    if (printItems) {
      const area = product.product?.areas[view];
      const parsed = typeof area === 'string'
        ? safeParseJson(area) as Record<string, unknown> | null
        : area && typeof area === 'object'
          ? area as unknown as Record<string, unknown>
          : null;
      const rect = resolvePrintAreaPx(parsed, stageW, stageH);
      if (rect) {
        ctx.drawImage(
          printItems,
          rect.left * renderScale,
          rect.top * renderScale,
          rect.width * renderScale,
          rect.height * renderScale,
        );
      }
    }
    return canvas;
  }

  /** Design items in printable area, stage pixel coords (same as DesignView.vue). */
  async function overlayDesignOnStageCanvas(
    ctx: CanvasRenderingContext2D,
    view: ViewName,
  ): Promise<void> {
    const items = design.items[view];
    if (!items?.length) return;
    const stageCanvas = await renderStageItemsCanvas(view);
    if (stageCanvas) ctx.drawImage(stageCanvas, 0, 0);
    const designCanvas = await renderView(view);
    if (!designCanvas) return;
    const area = product.product?.areas[view];
    let parsedArea: Record<string, unknown> | null = null;
    if (typeof area === 'string') {
      parsedArea = safeParseJson(area) as Record<string, unknown> | null;
    } else if (area && typeof area === 'object') {
      parsedArea = area as Record<string, unknown>;
    }
    const stageW = Math.max(1, Number(config.data.items.boxWidth) || 500);
    const stageH = Math.max(1, Number(config.data.items.boxHeight) || 500);
    const rect = resolvePrintAreaPx(parsedArea, stageW, stageH);
    if (!rect) return;
    ctx.drawImage(
      designCanvas,
      0,
      0,
      designCanvas.width,
      designCanvas.height,
      rect.left,
      rect.top,
      rect.width,
      rect.height,
    );
  }

  /** Multi-layer mockup from admin garmentLayers (% positions), same stage size as editor. */
  async function renderGarmentConfigMockup(
    view: ViewName,
    layers: GarmentLayerConfig[],
    stageW: number,
    stageH: number,
    colorIndex: number,
  ): Promise<HTMLCanvasElement> {
    const canvas = document.createElement('canvas');
    canvas.width = stageW;
    canvas.height = stageH;
    const ctx = canvas.getContext('2d')!;
    const c = config.data;
    const colorTintEnabled = c.items.colorTintEnabled === true;
    const activeColor = product.product?.colors[colorIndex] ?? product.activeColor;
    const tintHex = product.customTintHex ?? activeColor?.hex ?? product.tintHex;
    // Custom color has no catalog texture.
    const textureUrl = product.customTintHex ? '' : activeColor?.textureUrl ?? '';
    const sorted = [...layers]
      .map((layer, gIdx) => ({
        layer,
        z:
          layer.z != null && !Number.isNaN(Number(layer.z))
            ? Number(layer.z)
            : 10 * (gIdx + 1),
      }))
      .sort((a, b) => a.z - b.z);
    const veyop = (c.veyopUrl || '').replace(/\/$/, '');
    const site = (c.siteUrl || '').replace(/\/$/, '');
    for (const { layer } of sorted) {
      const urlRaw = String(layer.url ?? '').trim();
      if (!urlRaw) continue;
      const imgSrc = resolveProductImageSrc(urlRaw, veyop, site);
      try {
        const img = await loadImage(imgSrc);
        const left = ((Number(layer.left) || 0) / 100) * stageW;
        const top = ((Number(layer.top) || 0) / 100) * stageH;
        const lw = ((Number(layer.width) || 100) / 100) * stageW;
        const lh = ((Number(layer.height) || 100) / 100) * stageH;
        if (shouldTintLayer(layer, colorTintEnabled)) {
          const mode = layer.tintMode ?? 'multiply';
          const tex = textureUrl ? resolveProductImageSrc(textureUrl, veyop, site) : undefined;
          await drawTintedGarmentLayer(
            ctx,
            img,
            left,
            top,
            lw,
            lh,
            tintHexAtSlot(tintHex, layer.colorSlot ?? 0),
            tex,
            mode,
            loadImage,
          );
        } else {
          ctx.drawImage(img, left, top, lw, lh);
        }
      } catch {
        // Skip broken images
      }
    }
    await overlayDesignOnStageCanvas(ctx, view);
    await drawMaskImagesOnCanvas(ctx, c as unknown as Record<string, unknown>, view, stageW, stageH);
    return canvas;
  }

  async function renderSingleImageStageMockup(
    view: ViewName,
    imgUrl: string,
    stageW: number,
    stageH: number,
  ): Promise<HTMLCanvasElement | null> {
    try {
      const img = await loadImage(imgUrl);
      const canvas = document.createElement('canvas');
      canvas.width = stageW;
      canvas.height = stageH;
      const ctx = canvas.getContext('2d')!;
      drawImageContain(ctx, img, 0, 0, stageW, stageH);
      await overlayDesignOnStageCanvas(ctx, view);
      await drawMaskImagesOnCanvas(ctx, config.data as unknown as Record<string, unknown>, view, stageW, stageH);
      return canvas;
    } catch {
      return null;
    }
  }

  /**
   * Generate full product mockup canvas (product images + design overlay).
   */
  async function renderProductMockup(
    view: ViewName,
    colorIndex = product.activeColorIndex,
  ): Promise<HTMLCanvasElement | null> {
    const c = config.data;
    const stageW = Math.max(1, Number(c.items.boxWidth) || 500);
    const stageH = Math.max(1, Number(c.items.boxHeight) || 500);

    const garmentList = c.items.garmentLayers?.[view];
    if (Array.isArray(garmentList) && garmentList.length > 0) {
      return renderGarmentConfigMockup(view, garmentList, stageW, stageH, colorIndex);
    }

    const veyop = (c.veyopUrl || '').replace(/\/$/, '');
    const site = (c.siteUrl || '').replace(/\/$/, '');
    const colorIdx = colorIndex;
    const colorData = product.product?.colors[colorIdx];
    const fbRaw = fallbackBackgroundUrlView(
      view,
      c.items.viewImages,
      product.product?.image ?? '',
    );

    if (!colorData?.views?.[view]) {
      const fb = fbRaw ? resolveProductImageSrc(fbRaw, veyop, site) : '';
      return fb ? renderSingleImageStageMockup(view, fb, stageW, stageH) : null;
    }

    const rawView = colorData.views[view];
    const viewRows = tryParseViewLayerRows(rawView);

    if (!viewRows || viewRows.length === 0) {
      const rawStr = typeof rawView === 'string' ? rawView.trim() : '';
      let url = rawStr ? resolveColorViewImageUrl(rawStr, veyop, site) : '';
      if (!url && fbRaw) url = resolveProductImageSrc(fbRaw, veyop, site);
      return url ? renderSingleImageStageMockup(view, url, stageW, stageH) : null;
    }

    const viewLayers = viewRows;
    const maxW = Number(c.settings.max_box_width) || 600;
    const maxH = Number(c.settings.max_box_height) || 600;

    const canvas = document.createElement('canvas');
    canvas.width = maxW;
    canvas.height = maxH;
    const ctx = canvas.getContext('2d')!;

    const sorted = [...viewLayers].sort(
      (a: Record<string, unknown>, b: Record<string, unknown>) =>
        layerSortKey(a) - layerSortKey(b),
    );

    const productLayers = sorted.filter(
      (layer) => (layer as Record<string, unknown>).id !== 'area-design',
    );
    const hasDesignSlot = sorted.some(
      (layer) => (layer as Record<string, unknown>).id === 'area-design',
    );

    for (const layer of productLayers) {
      let imgSrc = String((layer as Record<string, string>).img || '');
      if (
        imgSrc &&
        !/^https?:\/\//i.test(imgSrc) &&
        !imgSrc.startsWith('data:') &&
        !imgSrc.startsWith('//')
      ) {
        imgSrc = imgSrc.startsWith('/')
          ? (c.siteUrl || '').replace(/\/$/, '') + imgSrc
          : `${(c.veyopUrl || '').replace(/\/$/, '')}/${imgSrc.replace(/^\//, '')}`;
      }
      if (imgSrc) {
        try {
          const img = await loadImage(imgSrc);
          const lLeft = parseFloat(
            String((layer as Record<string, unknown>).left ?? 0),
          );
          const lTop = parseFloat(
            String((layer as Record<string, unknown>).top ?? 0),
          );
          const lWidth = parseFloat(
            String((layer as Record<string, unknown>).width ?? img.width),
          );
          const lHeight = parseFloat(
            String((layer as Record<string, unknown>).height ?? img.height),
          );
          ctx.drawImage(img, lLeft, lTop, lWidth, lHeight);
        } catch {
          // Skip broken images
        }
      }
    }

    const areaRawLegacy = product.product?.areas[view];
    let parsedAreaLegacy: Record<string, unknown> | null = null;
    if (typeof areaRawLegacy === 'string') {
      parsedAreaLegacy = safeParseJson(areaRawLegacy) as Record<string, unknown> | null;
    } else if (areaRawLegacy && typeof areaRawLegacy === 'object') {
      parsedAreaLegacy = areaRawLegacy as Record<string, unknown>;
    }
    const areaRectStageLegacy = resolvePrintAreaPx(parsedAreaLegacy, stageW, stageH);
    const sx = maxW / stageW;
    const sy = maxH / stageH;

    const stageArtwork = await renderStageItemsCanvas(view);
    if (stageArtwork) {
      ctx.drawImage(stageArtwork, 0, 0, stageArtwork.width, stageArtwork.height, 0, 0, maxW, maxH);
    }

    function fitDesignInClipRect(
      dctx: CanvasRenderingContext2D,
      designCanvas: HTMLCanvasElement,
      boxLeft: number,
      boxTop: number,
      boxW: number,
      boxH: number,
    ) {
      const sw = designCanvas.width;
      const sh = designCanvas.height;
      let dw = sw;
      let dh = sh;
      if (dw > boxW) {
        dh = (dh * boxW) / dw;
        dw = boxW;
      }
      if (dh > boxH) {
        dw = (dw * boxH) / dh;
        dh = boxH;
      }
      const destLeft = boxLeft + (boxW - dw) / 2;
      const destTop = boxTop + (boxH - dh) / 2;

      const shapeId = inferPrintAreaShape(parsedAreaLegacy);
      dctx.save();
      dctx.translate(boxLeft, boxTop);
      applyClip(dctx, boxW, boxH, shapeId, resolvePrintAreaShapeParams(parsedAreaLegacy));
      dctx.translate(-boxLeft, -boxTop);
      dctx.drawImage(designCanvas, 0, 0, sw, sh, destLeft, destTop, dw, dh);
      dctx.restore();
    }

    /** Slot layer uses max-box px; catalog `areas` use stage px → scale when falling back. */
    function boxFromSlotOrStage(
      slot: Record<string, unknown> | undefined,
      designCanvas: HTMLCanvasElement,
      axis: 'left' | 'top' | 'width' | 'height',
    ): number {
      const slotKey = axis;
      const raw = slot?.[slotKey];
      if (raw != null && String(raw).trim() !== '') {
        return parseFloat(String(raw));
      }
      if (!areaRectStageLegacy) {
        return axis === 'width'
          ? designCanvas.width * sx
          : axis === 'height'
            ? designCanvas.height * sy
            : 0;
      }
      const stageVal = areaRectStageLegacy[axis];
      const scale = axis === 'left' || axis === 'width' ? sx : sy;
      return stageVal * scale;
    }

    if (hasDesignSlot) {
      const designCanvas = await renderView(view);
      if (designCanvas) {
        const slot = sorted.find(
          (layer) => (layer as Record<string, unknown>).id === 'area-design',
        ) as Record<string, unknown> | undefined;

        const boxLeft = boxFromSlotOrStage(slot, designCanvas, 'left');
        const boxTop = boxFromSlotOrStage(slot, designCanvas, 'top');
        let boxW = boxFromSlotOrStage(slot, designCanvas, 'width');
        let boxH = boxFromSlotOrStage(slot, designCanvas, 'height');
        if (!(boxW > 0)) boxW = designCanvas.width * sx;
        if (!(boxH > 0)) boxH = designCanvas.height * sy;

        fitDesignInClipRect(ctx, designCanvas, boxLeft, boxTop, boxW, boxH);
      }
    } else if (design.items[view]?.length && areaRectStageLegacy) {
      const designCanvas = await renderView(view);
      if (designCanvas) {
        const boxLeft = areaRectStageLegacy.left * sx;
        const boxTop = areaRectStageLegacy.top * sy;
        const boxW = areaRectStageLegacy.width * sx;
        const boxH = areaRectStageLegacy.height * sy;
        fitDesignInClipRect(ctx, designCanvas, boxLeft, boxTop, boxW, boxH);
      }
    }

    await drawMaskImagesOnCanvas(ctx, c as unknown as Record<string, unknown>, view, maxW, maxH);
    return canvas;
  }

  /**
   * Render all views (front, back, left, right) and store output.
   */
  async function renderAllViews(): Promise<Record<string, HTMLCanvasElement>> {
    const result: Record<string, HTMLCanvasElement> = {};
    for (const view of design.views) {
      const canvas = await renderProductMockup(view);
      if (canvas) {
        result[view] = canvas;
      }
    }
    return result;
  }

  /**
   * Cart / order PNG: full mockup (product layers + design area) when product JSON has view layers.
   */
  async function renderForCartExport(view: ViewName): Promise<HTMLCanvasElement | null> {
    const mockup = await renderProductMockup(view);
    if (mockup) {
      return mockup;
    }
    return renderStageArtwork(view);
  }

  /** Transparent print-area artwork at physical 300 DPI, without garment mockup. */
  async function renderForProductionExport(view: ViewName, dpi = 300): Promise<HTMLCanvasElement | null> {
    const area = product.product?.areas[view];
    const parsedArea = typeof area === 'string'
      ? safeParseJson(area) as Record<string, unknown> | null
      : area && typeof area === 'object'
        ? area as unknown as Record<string, unknown>
        : null;
    const stageW = Math.max(1, Number(config.data.items.boxWidth) || 500);
    const stageH = Math.max(1, Number(config.data.items.boxHeight) || 500);
    const rect = resolvePrintAreaPx(parsedArea, stageW, stageH);
    if (!rect) return null;
    const physical = resolveViewPhysicalSizesCm(product.product?.params)[view];
    const hasStageItems = (design.items[view] ?? []).some(
      (item) => item.placementSpace === 'product-stage' && isLayerVisible(item),
    );
    if (hasStageItems) {
      const stagePhysicalWidthCm = physical?.width
        ? physical.width * (stageW / Math.max(1, rect.width))
        : 0;
      const targetStageWidth = stagePhysicalWidthCm
        ? Math.max(stageW, Math.round((stagePhysicalWidthCm / 2.54) * Math.max(72, dpi)))
        : stageW * 4;
      return renderStageArtwork(view, 'production', targetStageWidth / stageW);
    }
    const targetWidth = physical?.width
      ? Math.max(rect.width, Math.round((physical.width / 2.54) * Math.max(72, dpi)))
      : rect.width * 4;
    return renderView(view, 'production', targetWidth / Math.max(1, rect.width));
  }

  return {
    renderView,
    renderStageArtwork,
    renderProductMockup,
    renderForCartExport,
    renderForProductionExport,
    renderAllViews,
  };
}

// ── Helpers ───────────────────────────────────────────────

// Decoded images are cached in the shared image pipeline (see loadImageBitmap).
function loadImage(src: string): Promise<HTMLImageElement> {
  return loadImageBitmap(src);
}

function applyClip(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  shapeId: string,
  params: Record<string, unknown>,
) {
  const shape = resolvePrintAreaShape(shapeId) ?? resolvePrintAreaShape('rect');
  if (!shape) return;
  ctx.beginPath();
  shape.clipCanvas(ctx, w, h, params);
  ctx.closePath();
  ctx.clip();
}
